import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

const UTILITY_KINDS = [
  ["electricity", "Electricity"], ["gas", "Gas"], ["water", "Water"], ["internet", "Internet"],
  ["council_tax", "Council tax"], ["refuse_collection", "Refuse collection"], ["tv_license", "TV licence"],
];
const ACCESS_TYPES = [["keypad", "Keypad"], ["key_safe", "Key safe"], ["smart_lock", "Smart lock"], ["spare_key", "Spare key"], ["other", "Other"]];
export const CERT_LABELS = {
  gas_safety: "Gas Safety", eicr: "EICR", epc: "EPC", fire_risk: "Fire Risk", pat_test: "PAT Test",
  legionella: "Legionella Risk", insurance: "Insurance", landlord_licence: "Landlord Licence",
  fire_alarm: "Fire Alarm", carbon_monoxide: "CO Alarm",
};

const sel = "mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs";

function Section({ title, onAdd, addTestId, children }) {
  return (
    <div className="pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <Label className="ns-label">{title}</Label>
        {onAdd && (
          <button type="button" onClick={onAdd} data-testid={addTestId}
            className="inline-flex items-center gap-1 text-[11px] text-teal-700 hover:text-teal-900 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

const RemoveBtn = ({ onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="text-slate-300 hover:text-red-600 transition-colors mt-5">
    <Trash2 className="w-4 h-4" />
  </button>
);

export function AccessPointsEditor({ value = [], onChange }) {
  const set = (i, patch) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Section title="Access points" addTestId="properties-access-add"
      onAdd={() => onChange([...value, { id: newId(), label: "", type: "key_safe", code: "", location_note: "" }])}>
      {value.length === 0 && <div className="text-xs text-slate-400 italic">No access points — add a key safe, smart lock or spare key.</div>}
      {value.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-[1fr_1fr_0.8fr_auto] gap-2 items-start" data-testid={`properties-access-row-${i}`}>
          <div><span className="text-[10px] text-slate-500">Label</span>
            <Input className="mt-1 h-9 bg-white text-xs" value={r.label} placeholder="Front door" data-testid={`properties-access-label-${i}`} onChange={(e) => set(i, { label: e.target.value })} /></div>
          <div><span className="text-[10px] text-slate-500">Type</span>
            <select className={sel} value={r.type} data-testid={`properties-access-type-${i}`} onChange={(e) => set(i, { type: e.target.value })}>
              {ACCESS_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><span className="text-[10px] text-slate-500">Code</span>
            <Input className="mt-1 h-9 bg-white text-xs font-mono" value={r.code} data-testid={`properties-access-code-${i}`} onChange={(e) => set(i, { code: e.target.value })} /></div>
          <RemoveBtn testId={`properties-access-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} />
          <Input className="col-span-3 h-8 bg-white text-xs" placeholder="Location note (e.g. left of bin store)" value={r.location_note || ""}
            data-testid={`properties-access-note-${i}`} onChange={(e) => set(i, { location_note: e.target.value })} />
        </div>
      ))}
    </Section>
  );
}

export function LeaseEditor({ value = {}, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <Section title="Lease">
      <div className="grid grid-cols-2 gap-2">
        <div><span className="text-[10px] text-slate-500">Start</span>
          <Input type="date" className="mt-1 h-9 bg-white text-xs" value={v.start_date || ""} data-testid="properties-lease-start" onChange={(e) => set({ start_date: e.target.value || null })} /></div>
        <div><span className="text-[10px] text-slate-500">End</span>
          <Input type="date" className="mt-1 h-9 bg-white text-xs" value={v.end_date || ""} data-testid="properties-lease-end" onChange={(e) => set({ end_date: e.target.value || null })} /></div>
        <div><span className="text-[10px] text-slate-500">Monthly rent paid (£)</span>
          <Input type="number" step="0.01" className="mt-1 h-9 bg-white text-xs" value={v.monthly_rent ?? ""} data-testid="properties-lease-rent" onChange={(e) => set({ monthly_rent: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
        <div><span className="text-[10px] text-slate-500">Landlord / tenant name</span>
          <Input className="mt-1 h-9 bg-white text-xs" value={v.tenant_name || ""} data-testid="properties-lease-tenant" onChange={(e) => set({ tenant_name: e.target.value })} /></div>
      </div>
      <div className="text-[11px] text-slate-400">Rent above £0 is booked as a recurring monthly expense.</div>
    </Section>
  );
}

export function UtilityAccountsEditor({ value = [], onChange }) {
  const set = (i, patch) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Section title="Utility accounts · recurring monthly" addTestId="properties-utility-add"
      onAdd={() => onChange([...value, { id: newId(), kind: "electricity", provider: "", account_number: "", monthly_cost: 0 }])}>
      {value.length === 0 && <div className="text-xs text-slate-400 italic">No utility accounts yet.</div>}
      {value.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-[1fr_1fr_1fr_0.7fr_auto] gap-2 items-start" data-testid={`properties-utility-row-${i}`}>
          <div><span className="text-[10px] text-slate-500">Type</span>
            <select className={sel} value={r.kind} data-testid={`properties-utility-kind-${i}`} onChange={(e) => set(i, { kind: e.target.value })}>
              {UTILITY_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><span className="text-[10px] text-slate-500">Provider</span>
            <Input className="mt-1 h-9 bg-white text-xs" value={r.provider} data-testid={`properties-utility-provider-${i}`} onChange={(e) => set(i, { provider: e.target.value })} /></div>
          <div><span className="text-[10px] text-slate-500">Account no.</span>
            <Input className="mt-1 h-9 bg-white text-xs font-mono" value={r.account_number} data-testid={`properties-utility-account-${i}`} onChange={(e) => set(i, { account_number: e.target.value })} /></div>
          <div><span className="text-[10px] text-slate-500">£ / month</span>
            <Input type="number" step="0.01" className="mt-1 h-9 bg-white text-xs" value={r.monthly_cost} data-testid={`properties-utility-cost-${i}`} onChange={(e) => set(i, { monthly_cost: e.target.value === "" ? 0 : parseFloat(e.target.value) })} /></div>
          <RemoveBtn testId={`properties-utility-remove-${i}`} onClick={() => onChange(value.filter((_, j) => j !== i))} />
        </div>
      ))}
    </Section>
  );
}

export function ComplianceEditor({ propertyId }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({ kind: "gas_safety", reference: "", issued_at: "", expires_at: "" });
  const load = () => api.get(`/compliance/${propertyId}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { if (propertyId) load(); /* eslint-disable-next-line */ }, [propertyId]);
  if (!propertyId) return <Section title="Compliance certificates"><div className="text-xs text-slate-400 italic">Save the property first to add certificates.</div></Section>;
  const add = async () => {
    try {
      await api.post(`/compliance/${propertyId}`, { ...draft, issued_at: draft.issued_at || null, expires_at: draft.expires_at || null });
      setDraft({ kind: "gas_safety", reference: "", issued_at: "", expires_at: "" });
      toast.success("Certificate added");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (id) => { await api.delete(`/compliance/${propertyId}/${id}`); load(); };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Section title="Compliance certificates">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-2 text-xs rounded-lg border border-slate-100 px-3 py-2" data-testid={`properties-compliance-entry-${r.id}`}>
          <div>
            <span className="font-medium">{CERT_LABELS[r.kind] || r.kind}</span>
            {r.reference && <span className="ml-2 font-mono text-slate-500">{r.reference}</span>}
          </div>
          <div className="flex items-center gap-2">
            <span className={r.expires_at && r.expires_at < today ? "text-rose-600" : "text-slate-500"}>{r.expires_at ? `exp ${r.expires_at}` : "no expiry"}</span>
            <button type="button" onClick={() => del(r.id)} data-testid={`properties-compliance-remove-${r.id}`} className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-[1fr_1fr_0.9fr_0.9fr_auto] gap-2 items-end">
        <select className={sel} value={draft.kind} data-testid="properties-compliance-kind" onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
          {Object.entries(CERT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Input className="h-9 bg-white text-xs" placeholder="Cert ref" value={draft.reference} data-testid="properties-compliance-ref" onChange={(e) => setDraft({ ...draft, reference: e.target.value })} />
        <Input type="date" className="h-9 bg-white text-xs" title="Issued" value={draft.issued_at} data-testid="properties-compliance-issued" onChange={(e) => setDraft({ ...draft, issued_at: e.target.value })} />
        <Input type="date" className="h-9 bg-white text-xs" title="Expires" value={draft.expires_at} data-testid="properties-compliance-expires" onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} />
        <button type="button" onClick={add} data-testid="properties-compliance-add" className="h-9 px-3 rounded-full bg-teal-700 text-white text-xs hover:bg-teal-800 transition-colors">Add</button>
      </div>
    </Section>
  );
}
